import React, { useEffect, useState } from 'react';
import { Input, Select, Button } from 'antd';
import { Plus, X } from 'lucide-react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';

const { Option } = Select;
const { TextArea } = Input;

interface BusinessHours {
	[key: string]: { open: string; close: string; closed: boolean };
}

interface PricingItem {
	id: string;
	name: string;
	price: string;
	description?: string;
}

interface FAQ {
	id: string;
	question: string;
	answer: string;
}

interface AddressItem {
	id: string;
	locationType: string;
	address: string;
}

interface OnboardingData {
	welcomeCompleted: boolean;
	businessName: string;
	phoneNumber: string;
	addresses: AddressItem[];
	businessHours: BusinessHours;
	pricing: PricingItem[];
	faqs: FAQ[];
	additionalInfo: string;
}

interface BusinessInfoReviewStageProps {
	onboardingData: OnboardingData;
	updateOnboardingData: (updates: Partial<OnboardingData>) => void;
}

const BusinessInfoReviewStage: React.FC<BusinessInfoReviewStageProps> = ({
	onboardingData,
	updateOnboardingData,
}) => {
	const [showValidationErrors, setShowValidationErrors] = useState(false);

	// Validation functions
	const validateBusinessName = () => !onboardingData.businessName.trim();
	const validatePhoneNumber = () => !onboardingData.phoneNumber.trim();
	const validateAddresses = () =>
		!onboardingData.addresses.length ||
		!onboardingData.addresses.some((addr) => addr.address.trim());

	const getFieldError = (fieldValidator: () => boolean, errorMessage: string) => {
		return showValidationErrors && fieldValidator() ? errorMessage : '';
	};

	// Add default address if none exist
	useEffect(() => {
		if (onboardingData.addresses.length === 0) {
			const defaultAddress: AddressItem = {
				id: Date.now().toString(),
				locationType: 'Address 1',
				address: '',
			};
			updateOnboardingData({
				addresses: [defaultAddress],
			});
		}
	}, [onboardingData.addresses.length, updateOnboardingData]);

	const addAddress = () => {
		const addressCount = onboardingData.addresses.length + 1;
		const newAddress: AddressItem = {
			id: Date.now().toString(),
			locationType: `Address ${addressCount}`,
			address: '',
		};
		updateOnboardingData({
			addresses: [...onboardingData.addresses, newAddress],
		});
	};

	const removeAddress = (id: string) => {
		const updatedAddresses = onboardingData.addresses
			.filter((a) => a.id !== id)
			.map((address, index) => ({
				...address,
				locationType: `Address ${index + 1}`,
			}));
		updateOnboardingData({
			addresses: updatedAddresses,
		});
	};

	const updateAddress = (id: string, field: keyof AddressItem, value: string) => {
		updateOnboardingData({
			addresses: onboardingData.addresses.map((a) => (a.id === id ? { ...a, [field]: value } : a)),
		});
	};

	const addPricing = () => {
		const newPricing: PricingItem = {
			id: Date.now().toString(),
			name: '',
			price: '',
		};
		updateOnboardingData({
			pricing: [...onboardingData.pricing, newPricing],
		});
	};

	const removePricing = (id: string) => {
		updateOnboardingData({
			pricing: onboardingData.pricing.filter((p) => p.id !== id),
		});
	};

	const updatePricing = (id: string, field: keyof PricingItem, value: string) => {
		updateOnboardingData({
			pricing: onboardingData.pricing.map((p) => (p.id === id ? { ...p, [field]: value } : p)),
		});
	};

	const addFAQ = () => {
		const newFAQ: FAQ = {
			id: Date.now().toString(),
			question: '',
			answer: '',
		};
		updateOnboardingData({
			faqs: [...onboardingData.faqs, newFAQ],
		});
	};

	const removeFAQ = (id: string) => {
		updateOnboardingData({
			faqs: onboardingData.faqs.filter((f) => f.id !== id),
		});
	};

	const updateFAQ = (id: string, field: keyof FAQ, value: string) => {
		updateOnboardingData({
			faqs: onboardingData.faqs.map((f) => (f.id === id ? { ...f, [field]: value } : f)),
		});
	};

	const updateBusinessHours = (day: string, field: string, value: any) => {
		const currentHours = onboardingData.businessHours[day];

		if (field === 'closed') {
			// When toggling open/closed, set appropriate default times
			updateOnboardingData({
				businessHours: {
					...onboardingData.businessHours,
					[day]: {
						...currentHours,
						closed: value,
						open: value ? '' : currentHours.open || '9:00 AM',
						close: value ? '' : currentHours.close || '5:00 PM',
					},
				},
			});
		} else {
			updateOnboardingData({
				businessHours: {
					...onboardingData.businessHours,
					[day]: { ...currentHours, [field]: value },
				},
			});
		}
	};

	// Listen for validation attempts from parent
	useEffect(() => {
		const handleValidation = () => {
			setShowValidationErrors(true);
		};

		window.addEventListener('trigger-validation', handleValidation);
		return () => window.removeEventListener('trigger-validation', handleValidation);
	}, []);

	// Clear validation errors when user starts typing
	useEffect(() => {
		if (showValidationErrors) {
			const hasErrors = validateBusinessName() || validatePhoneNumber() || validateAddresses();
			if (!hasErrors) {
				setShowValidationErrors(false);
			}
		}
	}, [
		onboardingData.businessName,
		onboardingData.phoneNumber,
		onboardingData.addresses,
		showValidationErrors,
	]);

	const timeOptions = [
		'12:00 AM',
		'12:30 AM',
		'1:00 AM',
		'1:30 AM',
		'2:00 AM',
		'2:30 AM',
		'3:00 AM',
		'3:30 AM',
		'4:00 AM',
		'4:30 AM',
		'5:00 AM',
		'5:30 AM',
		'6:00 AM',
		'6:30 AM',
		'7:00 AM',
		'7:30 AM',
		'8:00 AM',
		'8:30 AM',
		'9:00 AM',
		'9:30 AM',
		'10:00 AM',
		'10:30 AM',
		'11:00 AM',
		'11:30 AM',
		'12:00 PM',
		'12:30 PM',
		'1:00 PM',
		'1:30 PM',
		'2:00 PM',
		'2:30 PM',
		'3:00 PM',
		'3:30 PM',
		'4:00 PM',
		'4:30 PM',
		'5:00 PM',
		'5:30 PM',
		'6:00 PM',
		'6:30 PM',
		'7:00 PM',
		'7:30 PM',
		'8:00 PM',
		'8:30 PM',
		'9:00 PM',
		'9:30 PM',
		'10:00 PM',
		'10:30 PM',
		'11:00 PM',
		'11:30 PM',
	];

	return (
		<div className="max-w-2xl mx-auto px-4">
			{/* Friendly Header */}
			<div className="text-center mb-6">
				<h2 className="text-2xl font-bold text-blue-600 mb-3">
					Let's perfect your business details ✨
				</h2>
				<p className="text-gray-600 text-sm leading-relaxed">
					Take a moment to review and update the info we've found about your business.
				</p>
			</div>

			<div className="space-y-6">
				{/* Basic Information */}
				<div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-4 shadow-sm">
					<h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
						<span className="w-2 h-2 bg-blue-500 rounded-full mr-3" />
						Essential Details
					</h3>
					<div className="space-y-3">
						<div>
							<label className="block text-sm font-medium text-gray-700 mb-1">
								Business Name
								<span className="text-red-500 ml-1">*</span>
							</label>
							<Input
								value={onboardingData.businessName}
								onChange={(e) => updateOnboardingData({ businessName: e.target.value })}
								placeholder="Enter your awesome business name"
								className={`rounded-xl border-0 shadow-sm bg-white/90 backdrop-blur-sm hover:bg-white transition-all duration-200 ${
									getFieldError(validateBusinessName, 'Business name is required')
										? 'border-red-500 border-2'
										: ''
								}`}
								size="large"
								status={
									getFieldError(validateBusinessName, 'Business name is required') ? 'error' : ''
								}
							/>
							{getFieldError(validateBusinessName, 'Business name is required') && (
								<div className="text-red-500 text-xs mt-1 flex items-center">
									<span className="mr-1">⚠️</span>
									Business name is required
								</div>
							)}
						</div>

						<div>
							<label className="block text-sm font-medium text-gray-700 mb-1">
								Phone Number
								<span className="text-red-500 ml-1">*</span>
							</label>
							<div
								className={`rounded-xl shadow-sm bg-white/90 backdrop-blur-sm hover:bg-white transition-all duration-200 relative ${
									getFieldError(validatePhoneNumber, 'Phone number is required')
										? 'border-red-500 border-2'
										: ''
								}`}
								style={{ overflow: 'visible', zIndex: 50 }}
							>
								<PhoneInput
									placeholder="Enter Phone Number"
									country="us"
									value={
										typeof onboardingData.phoneNumber === 'string' ? onboardingData.phoneNumber : ''
									}
									onChange={(value: string) => updateOnboardingData({ phoneNumber: value })}
									preferredCountries={['us', 'ca', 'gb']}
									containerStyle={{
										width: '100%',
										position: 'relative',
										zIndex: 1000,
									}}
									inputStyle={{
										width: '100%',
										height: '40px',
										fontSize: '14px',
										border: 'none',
										background: 'transparent',
										fontFamily: 'DM Sans, sans-serif',
									}}
									buttonStyle={{
										border: 'none',
										background: 'transparent',
										borderRadius: '0.75rem 0 0 0.75rem',
									}}
									dropdownStyle={{
										zIndex: 9999,
										maxHeight: '200px',
										fontFamily: 'DM Sans, sans-serif',
									}}
								/>
							</div>
							{getFieldError(validatePhoneNumber, 'Phone number is required') && (
								<div className="text-red-500 text-xs mt-1 flex items-center">
									<span className="mr-1">⚠️</span>
									Phone number is required
								</div>
							)}
						</div>

						<div>
							<label className="block text-sm font-medium text-gray-700 mb-1">
								Business Address
								<span className="text-red-500 ml-1">*</span>
							</label>
							<div className="space-y-2">
								{onboardingData.addresses.map((addressItem) => (
									<div key={addressItem.id} className="relative group">
										<TextArea
											value={addressItem.address}
											onChange={(e) => updateAddress(addressItem.id, 'address', e.target.value)}
											placeholder="Enter your business address"
											rows={2}
											className={`rounded-xl border-0 shadow-sm bg-white/90 backdrop-blur-sm hover:bg-white transition-all duration-200 resize-none ${
												getFieldError(validateAddresses, 'At least one address is required')
													? 'border-red-500 border-2'
													: ''
											}`}
											status={
												getFieldError(validateAddresses, 'At least one address is required')
													? 'error'
													: ''
											}
										/>
										{onboardingData.addresses.length > 1 && (
											<Button
												type="text"
												size="small"
												onClick={() => removeAddress(addressItem.id)}
												className="absolute top-2 right-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-200"
											>
												<X className="w-4 h-4" />
											</Button>
										)}
									</div>
								))}
								{getFieldError(validateAddresses, 'At least one address is required') && (
									<div className="text-red-500 text-xs mt-1 flex items-center">
										<span className="mr-1">⚠️</span>
										At least one address is required
									</div>
								)}
								<Button
									type="dashed"
									onClick={addAddress}
									className="w-full h-10 rounded-xl border-2 border-blue-200 border-dashed text-blue-600 hover:border-blue-300 hover:bg-blue-50 transition-all duration-200"
								>
									<Plus className="w-4 h-4 mr-2" />
									Add another location
								</Button>
							</div>
						</div>
					</div>
				</div>

				{/* Business Hours */}
				<div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-4 shadow-sm">
					<h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
						<span className="w-2 h-2 bg-green-500 rounded-full mr-3" />
						When You're Open
					</h3>
					<div className="space-y-2">
						{Object.entries(onboardingData.businessHours)
							.slice(0, 7)
							.map(([day, hours]) => (
								<div
									key={day}
									className="bg-white/90 backdrop-blur-sm rounded-xl p-3 hover:bg-white border border-white/50 hover:border-white/80 transition-all duration-200"
								>
									{/* Mobile Layout */}
									<div className="sm:hidden">
										<div className="flex items-center justify-between mb-3">
											<div className="text-sm text-gray-700 capitalize font-medium">{day}</div>
											<Button
												type="text"
												size="small"
												onClick={() => updateBusinessHours(day, 'closed', !hours.closed)}
												className={`px-3 py-1 rounded-full font-medium transition-all duration-200 ${
													hours.closed
														? 'text-green-600 hover:bg-green-100'
														: 'text-orange-600 hover:bg-orange-100'
												}`}
											>
												{hours.closed ? 'Open' : 'Close'}
											</Button>
										</div>
										{hours.closed ? (
											<div className="text-center text-gray-400 font-medium py-2">Closed</div>
										) : (
											<div className="flex items-center justify-center gap-2">
												<Select
													value={hours.open}
													onChange={(value) => updateBusinessHours(day, 'open', value)}
													className="flex-1 max-w-[120px] rounded-lg"
													placeholder="Open"
													size="middle"
												>
													{timeOptions.map((time) => (
														<Option key={time} value={time}>
															{time}
														</Option>
													))}
												</Select>
												<span className="text-gray-400 font-medium px-1">to</span>
												<Select
													value={hours.close}
													onChange={(value) => updateBusinessHours(day, 'close', value)}
													className="flex-1 max-w-[120px] rounded-lg"
													placeholder="Close"
													size="middle"
												>
													{timeOptions.map((time) => (
														<Option key={time} value={time}>
															{time}
														</Option>
													))}
												</Select>
											</div>
										)}
									</div>

									{/* Desktop Layout */}
									<div className="hidden sm:flex items-center justify-between">
										<div className="w-20 text-sm text-gray-700 capitalize font-medium">{day}</div>
										{hours.closed ? (
											<div className="flex-1 text-center text-gray-400 font-medium">Closed</div>
										) : (
											<div className="flex-1 flex items-center justify-center gap-3">
												<Select
													value={hours.open}
													onChange={(value) => updateBusinessHours(day, 'open', value)}
													className="w-28 rounded-lg"
													placeholder="Open"
													size="middle"
												>
													{timeOptions.map((time) => (
														<Option key={time} value={time}>
															{time}
														</Option>
													))}
												</Select>
												<span className="text-gray-400 font-medium">to</span>
												<Select
													value={hours.close}
													onChange={(value) => updateBusinessHours(day, 'close', value)}
													className="w-28 rounded-lg"
													placeholder="Close"
													size="middle"
												>
													{timeOptions.map((time) => (
														<Option key={time} value={time}>
															{time}
														</Option>
													))}
												</Select>
											</div>
										)}
										<Button
											type="text"
											size="small"
											onClick={() => updateBusinessHours(day, 'closed', !hours.closed)}
											className={`ml-4 px-3 py-1 rounded-full font-medium transition-all duration-200 ${
												hours.closed
													? 'text-green-600 hover:bg-green-100'
													: 'text-orange-600 hover:bg-orange-100'
											}`}
										>
											{hours.closed ? 'Open' : 'Close'}
										</Button>
									</div>
								</div>
							))}
					</div>
				</div>

				{/* Pricing */}
				<div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl p-4 shadow-sm">
					<div className="mb-4">
						<h3 className="text-lg font-semibold text-gray-800 mb-1 flex items-center">
							<span className="w-2 h-2 bg-purple-500 rounded-full mr-3" />
							Your Pricing
						</h3>
					</div>
					<div className="space-y-2">
						{onboardingData.pricing.map((item) => (
							<div
								key={item.id}
								className="flex gap-3 items-center bg-white/90 backdrop-blur-sm rounded-xl p-3 hover:bg-white border border-white/50 hover:border-white/80 transition-all duration-200 group"
							>
								<Input
									value={item.name}
									onChange={(e) => updatePricing(item.id, 'name', e.target.value)}
									placeholder="Service or product name"
									className="flex-1 border-0 shadow-none bg-transparent"
									size="middle"
								/>
								<Input
									value={item.price}
									onChange={(e) => updatePricing(item.id, 'price', e.target.value)}
									placeholder="$99"
									className="w-24 border-0 shadow-none bg-transparent text-center"
									size="middle"
								/>
								<Button
									type="text"
									size="small"
									onClick={() => removePricing(item.id)}
									className="text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-200"
								>
									<X className="w-4 h-4" />
								</Button>
							</div>
						))}
						<Button
							type="dashed"
							onClick={addPricing}
							className="w-full h-10 rounded-xl border-2 border-purple-200 border-dashed text-purple-600 hover:border-purple-300 hover:bg-purple-50 transition-all duration-200"
						>
							<Plus className="w-4 h-4 mr-2" />
							Add pricing
						</Button>
					</div>
				</div>

				{/* FAQs */}
				<div className="bg-gradient-to-r from-orange-50 to-yellow-50 rounded-2xl p-4 shadow-sm">
					<div className="mb-4">
						<h3 className="text-lg font-semibold text-gray-800 mb-1 flex items-center">
							<span className="w-2 h-2 bg-orange-500 rounded-full mr-3" />
							Common Questions
						</h3>
					</div>
					<div className="space-y-3">
						{onboardingData.faqs.map((faq) => (
							<div
								key={faq.id}
								className="bg-white/90 backdrop-blur-sm rounded-xl p-3 hover:bg-white border border-white/50 hover:border-white/80 transition-all duration-200 group"
							>
								<div className="flex gap-3 items-start mb-2">
									<Input
										value={faq.question}
										onChange={(e) => updateFAQ(faq.id, 'question', e.target.value)}
										placeholder="What question do customers ask?"
										className="flex-1 border-0 shadow-none bg-transparent"
										size="middle"
									/>
									<Button
										type="text"
										size="small"
										onClick={() => removeFAQ(faq.id)}
										className="text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-200"
									>
										<X className="w-4 h-4" />
									</Button>
								</div>
								<TextArea
									value={faq.answer}
									onChange={(e) => updateFAQ(faq.id, 'answer', e.target.value)}
									placeholder="How would you answer this?"
									rows={2}
									className="border-0 shadow-none bg-transparent resize-none"
								/>
							</div>
						))}
						<Button
							type="dashed"
							onClick={addFAQ}
							className="w-full h-10 rounded-xl border-2 border-orange-200 border-dashed text-orange-600 hover:border-orange-300 hover:bg-orange-50 transition-all duration-200"
						>
							<Plus className="w-4 h-4 mr-2" />
							Add question
						</Button>
					</div>
				</div>

				{/* Additional Information */}
				<div className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-2xl p-4 shadow-sm">
					<div className="mb-4">
						<h3 className="text-lg font-semibold text-gray-800 mb-1 flex items-center">
							<span className="w-2 h-2 bg-gray-500 rounded-full mr-3" />
							Extra Details
						</h3>
					</div>
					<TextArea
						value={onboardingData.additionalInfo}
						onChange={(e) => updateOnboardingData({ additionalInfo: e.target.value })}
						rows={4}
						placeholder="Tell us something special about your business..."
						className="rounded-xl border border-white/50 shadow-sm bg-white/90 backdrop-blur-sm hover:bg-white hover:border-white/80 transition-all duration-200 resize-none"
					/>
				</div>
			</div>
		</div>
	);
};

export default BusinessInfoReviewStage;
